import { useState } from 'react';
import { createPortal } from 'react-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link2, Plus, Repeat, Unlink, X } from 'lucide-react';
import { PnlApiError } from '@pnl/sdk';
import { Spinner } from '@pnl/ui';
import { api } from '../../lib/api';
import { formatBrl } from '../../lib/format';
import { Card } from './Card';

// "Conquistar outra conta" leva pro Novo Challenge (mesmo destino da sidebar):
// é assim que o trader ganha mais uma conta funded pra replicar.
const NOVO_CHALLENGE_URL = 'https://pnlglobalb3.com.br/planos';
const MAX_ACCOUNTS = 4;

/**
 * Modal "Adicionar conta ao replicador". Lista as contas funded do MESMO tamanho da
 * principal (ainda não vinculadas) pra escolher — ou, se não houver, mostra uma
 * mensagem + botão pra conquistar uma nova conta nos planos.
 *
 * 1ª versão: o vínculo é REGISTRADO/EXIBIDO; o espelhamento automático de ordens
 * (engine que conversa com a Nelogica) entra em breve.
 */
function AddAccountModal({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const syncable = useQuery({
    queryKey: ['trader-syncable'],
    queryFn: () => api.getSyncableAccounts(),
  });
  const sync = useMutation({
    mutationFn: (id: string) => api.syncReplicatorAccount(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['trader-me'] });
      void qc.invalidateQueries({ queryKey: ['trader-syncable'] });
      onClose();
    },
    onError: (err: unknown) => {
      setError(err instanceof PnlApiError ? err.api.message : 'Não foi possível vincular a conta.');
      void qc.invalidateQueries({ queryKey: ['trader-syncable'] });
    },
  });

  const items = syncable.data?.items ?? [];

  return createPortal(
    <div
      className="bg-ink-950/70 fixed inset-0 z-50 flex items-center justify-center p-4 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="border-ink-700 bg-ink-900 w-full max-w-md rounded-xl border shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="border-ink-800 flex items-center justify-between border-b px-5 py-4">
          <div>
            <p className="text-2xs text-ink-400 font-semibold uppercase tracking-widest">
              Replicador
            </p>
            <h3 className="text-ink-100 text-base font-semibold">Adicionar conta</h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Fechar"
            className="text-ink-400 hover:text-ink-100"
          >
            <X size={18} />
          </button>
        </div>

        <div className="px-5 py-4">
          {syncable.isLoading ? (
            <div className="flex min-h-[120px] items-center justify-center">
              <Spinner size={20} />
            </div>
          ) : items.length > 0 ? (
            <>
              <p className="text-ink-300 text-sm">
                Selecione uma conta funded do mesmo tamanho para replicar a sua conta principal:
              </p>
              <ul className="mt-3 space-y-2">
                {items.map((a) => (
                  <li
                    key={a.id}
                    className="border-ink-800 bg-ink-950/40 flex items-center justify-between gap-3 rounded-lg border px-3 py-2.5"
                  >
                    <div className="min-w-0">
                      <p className="text-ink-100 truncate text-sm font-medium">{a.label}</p>
                      <p className="text-ink-500 text-xs tabular-nums">
                        {formatBrl(a.capitalCents)}
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        setError(null);
                        sync.mutate(a.id);
                      }}
                      disabled={sync.isPending}
                      className="bg-brand-500 hover:bg-brand-600 inline-flex shrink-0 items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-semibold text-white transition-colors disabled:opacity-60"
                    >
                      <Link2 size={13} /> Vincular
                    </button>
                  </li>
                ))}
              </ul>
              {error && <p className="text-danger-300 mt-3 text-xs">{error}</p>}
              <p className="text-2xs text-ink-500 mt-3 leading-relaxed">
                A conta passará a aparecer como Conta Replicada. O espelhamento automático de ordens
                será ativado em breve.
              </p>
            </>
          ) : (
            <>
              <p className="text-ink-300 text-sm leading-relaxed">
                Você ainda não tem outra conta funded do mesmo tamanho para vincular. Conquiste uma
                nova conta para replicar suas ordens.
              </p>
              <a
                href={NOVO_CHALLENGE_URL}
                target="_blank"
                rel="noreferrer"
                className="bg-brand-500 hover:bg-brand-600 mt-4 inline-flex w-full items-center justify-center gap-2 rounded-md py-2.5 text-sm font-semibold text-white transition-colors"
              >
                <Plus size={15} /> Ver planos
              </a>
            </>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}

/**
 * Estado "ainda não liberado" do Replicador — mostrado a quem não tem conta funded
 * (ou ainda não tem linhas). Existe pra que o dashboard fique COMPLETO desde a
 * primeira conta em teste: o card explica o recurso e como liberá-lo, em vez de
 * desaparecer e deixar meia grade vazia.
 */
function ReplicatorLockedCard({ loading }: { loading: boolean }) {
  return (
    <Card
      title="Replicador de Ordens PnL"
      action={
        <span className="text-2xs bg-ink-800 text-ink-400 inline-flex items-center gap-1 rounded-full px-2 py-0.5 font-semibold">
          <Repeat size={11} /> Disponível no Funded
        </span>
      }
    >
      <div className="px-5 py-4">
        {loading ? (
          <div className="flex min-h-[120px] items-center justify-center">
            <Spinner size={20} />
          </div>
        ) : (
          <>
            <p className="text-ink-300 text-sm leading-relaxed">
              O Replicador espelha automaticamente as suas ordens da conta principal em até{' '}
              {MAX_ACCOUNTS} contas funded — você opera uma vez e multiplica o resultado.
            </p>
            <ul className="text-ink-400 mt-3 space-y-1.5 text-xs">
              <li className="flex items-start gap-2">
                <span className="text-brand-400 mt-px">•</span>
                Liberado assim que você for aprovado e tiver a sua conta funded.
              </li>
              <li className="flex items-start gap-2">
                <span className="text-brand-400 mt-px">•</span>
                Cada conta adicional aparece aqui com o resultado do dia e o total somado.
              </li>
            </ul>
            <a
              href={NOVO_CHALLENGE_URL}
              target="_blank"
              rel="noreferrer"
              className="text-brand-400 hover:text-brand-300 mt-4 inline-flex items-center gap-1 text-xs font-semibold"
            >
              <Plus size={13} /> Conhecer os planos
            </a>
          </>
        )}
      </div>
    </Card>
  );
}

/**
 * Card do Replicador no topo do dashboard (briefing "Bloco 5"). Lista a Conta
 * Principal + — só com o replicador ATIVO (feature paga) — as Contas Replicadas
 * (contas vinculadas), cada uma com o disponível para saque, + total + "adicionar
 * mais uma conta" (máximo de 4). Sem conta funded, cai no card "locked" acima.
 */
export function ReplicatorCard() {
  const qc = useQueryClient();
  const repl = useQuery({
    queryKey: ['trader-replicator'],
    queryFn: () => api.getReplicatorStatus(),
  });
  const me = useQuery({ queryKey: ['trader-me'], queryFn: () => api.getTraderMe() });
  const [modalOpen, setModalOpen] = useState(false);

  const unsync = useMutation({
    mutationFn: (id: string) => api.unsyncReplicatorAccount(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['trader-me'] });
      void qc.invalidateQueries({ queryKey: ['trader-syncable'] });
    },
  });

  const funded = (me.data?.accounts ?? []).filter(
    (a) => (a.phase === 'FUNDED' || a.phase === 'LIVE') && a.state === 'ACTIVE',
  );

  const active = repl.data?.active ?? false;
  // As linhas vêm RESOLVIDAS do backend (principal + réplicas) e trazem o
  // RESULTADO DO DIA — que é o que o replicador espelha. Com o espelho ok, a
  // principal e as réplicas mostram o mesmo valor.
  const rows = repl.data?.accounts ?? [];

  // O card aparece pra TODO trader (pedido do cliente): quem ainda não tem conta
  // funded vê o que é o recurso e como liberar, em vez do card sumir e deixar um
  // buraco na grade ao lado da Pontuação de Disciplina.
  if (funded.length === 0 || rows.length === 0) {
    return <ReplicatorLockedCard loading={me.isLoading || repl.isLoading} />;
  }

  const total = rows.reduce((sum, r) => sum + r.dayResultCents, 0);
  // "Adicionar conta" só com o replicador ativo e abaixo do limite de contas.
  const canAdd = active && funded.length < MAX_ACCOUNTS;

  return (
    <Card
      title="Replicador de Ordens PnL"
      action={
        <span
          className={
            'text-2xs inline-flex items-center gap-1 rounded-full px-2 py-0.5 font-semibold ' +
            (active ? 'bg-success-500/15 text-success-300' : 'bg-ink-800 text-ink-400')
          }
        >
          <Repeat size={11} /> {active ? 'Replicador ativo' : 'Replicador inativo'}
        </span>
      }
    >
      <div className="px-5 py-4">
        {/* O replicador é do TRADER, não da conta aberta: mostra sempre as contas
            vinculadas nele. Sem essa legenda, abrir uma conta de teste e ver os
            números das contas funded parecia dado errado (feedback do cliente). */}
        <p className="text-2xs text-ink-400 mb-2.5 leading-relaxed">
          Resultado do dia nas suas contas do replicador — independe da conta aberta acima.
        </p>
        <ul className="divide-ink-800 divide-y">
          {rows.map((r) => (
            <li key={r.id} className="flex items-center justify-between gap-3 py-2.5">
              {/* Desvincular fica à ESQUERDA (pedido do cliente): assim a coluna
                  de valores da direita alinha uma abaixo da outra. */}
              <div className="flex min-w-0 items-center gap-2">
                {r.role === 'REPLICA' ? (
                  <button
                    type="button"
                    onClick={() => unsync.mutate(r.id)}
                    disabled={unsync.isPending}
                    aria-label="Desvincular conta"
                    title="Desvincular"
                    className="text-ink-500 hover:text-danger-300 shrink-0 transition-colors disabled:opacity-60"
                  >
                    <Unlink size={14} />
                  </button>
                ) : (
                  <span aria-hidden className="w-[14px] shrink-0" />
                )}
                <div className="min-w-0">
                  <p className="text-ink-100 text-sm font-medium">
                    {r.role === 'PRINCIPAL' ? 'Conta Principal' : 'Conta Replicada'}
                  </p>
                  <p className="text-ink-500 truncate text-xs">{r.label}</p>
                </div>
              </div>
              <span
                className={
                  'shrink-0 text-sm font-semibold tabular-nums ' +
                  (r.dayResultCents < 0 ? 'text-danger-300' : 'text-success-300')
                }
              >
                {formatBrl(r.dayResultCents, { signed: true })}
              </span>
            </li>
          ))}
        </ul>

        <div className="border-ink-800 mt-1 flex items-center justify-between border-t pt-3">
          <span className="text-ink-200 text-sm font-semibold">Total</span>
          <span
            className={
              'shrink-0 text-base font-bold tabular-nums ' +
              (total < 0 ? 'text-danger-300' : 'text-success-300')
            }
          >
            {formatBrl(total, { signed: true })}
          </span>
        </div>

        {canAdd && (
          <button
            type="button"
            onClick={() => setModalOpen(true)}
            className="text-brand-400 hover:text-brand-300 mt-3 inline-flex items-center gap-1 text-xs font-semibold"
          >
            <Plus size={13} /> Adicionar mais uma conta
          </button>
        )}
      </div>

      {modalOpen && <AddAccountModal onClose={() => setModalOpen(false)} />}
    </Card>
  );
}
